/**
 * Path Helper Utilities
 * 
 * Low-level functions for path calculations and collision detection.
 * 
 * ENHANCED: Includes gap-aware routing that finds actual gaps between nodes
 * and routes connections through them, dividing gap space evenly when
 * multiple connections need to pass through the same gap.
 */

import { NODE_WIDTH, NODE_HEIGHT, ROUTING_OBSTACLE_INFLATION } from './constants';

const MIN_GAP_PADDING = 6;

/** Build once per edge call; segment queries return a deduped subset of obstacles near the segment. */
const DEFAULT_SPATIAL_CELL = 240;

/**
 * Minimum graph size before building a uniform grid for obstacle checks.
 * Below this, full scans are cheaper than grid overhead.
 */
export const OBSTACLE_SPATIAL_INDEX_MIN_NODES = 40;

/** Set by `runWithObstacleSpatialIndex({ index, obstacleRefSet }, fn)` during downward routing. */
let activeObstacleSpatialIndex = null;

/**
 * Run a synchronous callback with obstacle spatial context for pathBlockedByNodes.
 * `context` is `{ index, obstacleRefSet }` where `obstacleRefSet` is a Set of node objects
 * that may block this edge (same references as in `obstacles` passed to routing).
 */
export function runWithObstacleSpatialIndex(context, fn) {
  const prev = activeObstacleSpatialIndex;
  activeObstacleSpatialIndex = context;
  try {
    return fn();
  } finally {
    activeObstacleSpatialIndex = prev;
  }
}

/**
 * @returns {{ cellSize: number, bucket: Map<string, Array<object>>, maxW: number, maxH: number } | null}
 */
export function buildObstacleSpatialIndex(obstacles, cellSize = DEFAULT_SPATIAL_CELL) {
  if (!obstacles || obstacles.length === 0) return null;

  const bucket = new Map();
  const pad = ROUTING_OBSTACLE_INFLATION;
  const maxW = Math.max(NODE_WIDTH, 360) + pad * 2;
  const maxH = Math.max(NODE_HEIGHT, 220) + pad * 2;

  for (const node of obstacles) {
    if (!node.pos || !node.dims) continue;
    const w = node.dims.width || NODE_WIDTH;
    const h = node.dims.height || NODE_HEIGHT;
    const left = node.pos.x - pad;
    const right = node.pos.x + w + pad;
    const top = node.pos.y - pad;
    const bottom = node.pos.y + h + pad;

    const i0 = Math.floor(left / cellSize);
    const i1 = Math.floor(right / cellSize);
    const j0 = Math.floor(top / cellSize);
    const j1 = Math.floor(bottom / cellSize);

    for (let i = i0; i <= i1; i++) {
      for (let j = j0; j <= j1; j++) {
        const key = `${i},${j}`;
        let arr = bucket.get(key);
        if (!arr) {
          arr = [];
          bucket.set(key, arr);
        }
        arr.push(node);
      }
    }
  }

  return { cellSize, bucket, maxW, maxH };
}

function candidatesForOrthogonalSegment(index, x1, y1, x2, y2) {
  const { cellSize, bucket, maxW, maxH } = index;
  const minX0 = Math.min(x1, x2);
  const maxX0 = Math.max(x1, x2);
  const minY0 = Math.min(y1, y2);
  const maxY0 = Math.max(y1, y2);

  let qLeft;
  let qRight;
  let qTop;
  let qBottom;

  if (Math.abs(x1 - x2) < 1) {
    const x = x1;
    qLeft = x - maxW;
    qRight = x + maxW;
    qTop = minY0;
    qBottom = maxY0;
  } else if (Math.abs(y1 - y2) < 1) {
    const y = y1;
    qLeft = minX0;
    qRight = maxX0;
    qTop = y - maxH;
    qBottom = y + maxH;
  } else {
    qLeft = minX0;
    qRight = maxX0;
    qTop = minY0;
    qBottom = maxY0;
  }

  const i0 = Math.floor(qLeft / cellSize);
  const i1 = Math.floor(qRight / cellSize);
  const j0 = Math.floor(qTop / cellSize);
  const j1 = Math.floor(qBottom / cellSize);

  const seen = new Set();
  const out = [];
  for (let i = i0; i <= i1; i++) {
    for (let j = j0; j <= j1; j++) {
      const cell = bucket.get(`${i},${j}`);
      if (!cell) continue;
      for (const node of cell) {
        if (seen.has(node)) continue;
        seen.add(node);
        out.push(node);
      }
    }
  }
  return out;
}

function segmentHitTest(x1, y1, x2, y2, node, pad) {
  if (!node.pos || !node.dims) return false;
  const nodeLeft = node.pos.x - pad;
  const nodeRight = node.pos.x + (node.dims.width || NODE_WIDTH) + pad;
  const nodeTop = node.pos.y - pad;
  const nodeBottom = node.pos.y + (node.dims.height || NODE_HEIGHT) + pad;

  if (Math.abs(x1 - x2) < 1) {
    const x = x1;
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    if (x >= nodeLeft && x <= nodeRight && maxY >= nodeTop && minY <= nodeBottom) {
      return true;
    }
  } else if (Math.abs(y1 - y2) < 1) {
    const y = y1;
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    if (y >= nodeTop && y <= nodeBottom && maxX >= nodeLeft && minX <= nodeRight) {
      return true;
    }
  }
  return false;
}

/**
 * Calculate safe corner radius that won't exceed available space
 */
export function safeRadius(r, ...distances) {
  const minDistance = Math.min(...distances.map(d => Math.abs(d)));
  return Math.max(0, Math.min(r, minDistance / 2));
}

/**
 * Check if a straight line (vertical or horizontal) passes through any node
 */
export function pathBlockedByNodes(x1, y1, x2, y2, obstacles, edgeKey = null) {
  const pad = ROUTING_OBSTACLE_INFLATION;
  const ctx = activeObstacleSpatialIndex;

  if (ctx && ctx.index) {
    const candidates = candidatesForOrthogonalSegment(ctx.index, x1, y1, x2, y2);
    const set = ctx.obstacleRefSet;
    for (const node of candidates) {
      if (set && !set.has(node)) continue;
      if (segmentHitTest(x1, y1, x2, y2, node, pad)) {
        return true;
      }
    }
    return false;
  }

  for (const node of obstacles) {
    if (segmentHitTest(x1, y1, x2, y2, node, pad)) {
      return true;
    }
  }
  return false;
}

/**
 * Find all clear gaps for a vertical line spanning y1..y2 among the obstacles.
 * Returns sorted array of { left, right } representing clear corridors.
 */
function findVerticalGaps(y1, y2, obstacles) {
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);
  const pad = ROUTING_OBSTACLE_INFLATION;

  // Collect all node boundaries that overlap vertically with our segment
  const blockers = [];
  for (const node of obstacles) {
    if (!node.pos || !node.dims) continue;
    const nodeTop = node.pos.y - pad;
    const nodeBottom = node.pos.y + (node.dims.height || NODE_HEIGHT) + pad;

    if (maxY > nodeTop && minY < nodeBottom) {
      blockers.push({
        left: node.pos.x - pad,
        right: node.pos.x + (node.dims.width || NODE_WIDTH) + pad,
      });
    }
  }
  
  if (blockers.length === 0) {
    return [{ left: -Infinity, right: Infinity }];
  }
  
  // Sort by left edge
  blockers.sort((a, b) => a.left - b.left);
  
  // Merge overlapping blockers
  const merged = [blockers[0]];
  for (let i = 1; i < blockers.length; i++) {
    const last = merged[merged.length - 1];
    if (blockers[i].left <= last.right) {
      last.right = Math.max(last.right, blockers[i].right);
    } else {
      merged.push({ ...blockers[i] });
    }
  }
  
  // Build gaps between merged blockers
  const gaps = [];
  gaps.push({ left: -Infinity, right: merged[0].left });
  for (let i = 0; i < merged.length - 1; i++) {
    gaps.push({ left: merged[i].right, right: merged[i + 1].left });
  }
  gaps.push({ left: merged[merged.length - 1].right, right: Infinity });
  
  return gaps;
}

/**
 * Find all clear gaps for a horizontal line spanning x1..x2 among the obstacles.
 * Returns sorted array of { top, bottom } representing clear corridors.
 */
function findHorizontalGaps(x1, x2, obstacles) {
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const pad = ROUTING_OBSTACLE_INFLATION;

  const blockers = [];
  for (const node of obstacles) {
    if (!node.pos || !node.dims) continue;
    const nodeLeft = node.pos.x - pad;
    const nodeRight = node.pos.x + (node.dims.width || NODE_WIDTH) + pad;

    if (maxX > nodeLeft && minX < nodeRight) {
      blockers.push({
        top: node.pos.y - pad,
        bottom: node.pos.y + (node.dims.height || NODE_HEIGHT) + pad,
      });
    }
  }
  
  if (blockers.length === 0) {
    return [{ top: -Infinity, bottom: Infinity }];
  }
  
  blockers.sort((a, b) => a.top - b.top);
  
  const merged = [blockers[0]];
  for (let i = 1; i < blockers.length; i++) {
    const last = merged[merged.length - 1];
    if (blockers[i].top <= last.bottom) {
      last.bottom = Math.max(last.bottom, blockers[i].bottom);
    } else {
      merged.push({ ...blockers[i] });
    }
  }
  
  const gaps = [];
  gaps.push({ top: -Infinity, bottom: merged[0].top });
  for (let i = 0; i < merged.length - 1; i++) {
    gaps.push({ top: merged[i].bottom, bottom: merged[i + 1].top });
  }
  gaps.push({ top: merged[merged.length - 1].bottom, bottom: Infinity });
  
  return gaps;
}

/**
 * Find the best gap for a vertical line at preferredX.
 * Prefers the gap that contains preferredX, otherwise the nearest gap.
 */
function findBestVerticalGap(preferredX, y1, y2, obstacles) {
  const gaps = findVerticalGaps(y1, y2, obstacles);
  
  // First check if preferredX is already in a gap
  for (const gap of gaps) {
    if (preferredX > gap.left + MIN_GAP_PADDING && preferredX < gap.right - MIN_GAP_PADDING) {
      return { gap, x: preferredX };
    }
  }
  
  // Find nearest gap
  let bestGap = null;
  let bestX = preferredX;
  let bestDist = Infinity;
  
  for (const gap of gaps) {
    const gapWidth = gap.right - gap.left;
    if (!Number.isFinite(gapWidth) || gapWidth < MIN_GAP_PADDING * 2) {
      // For infinite or very narrow gaps, use the finite edge
      if (Number.isFinite(gap.right) && !Number.isFinite(gap.left)) {
        const x = gap.right - ROUTING_OBSTACLE_INFLATION;
        const dist = Math.abs(x - preferredX);
        if (dist < bestDist) { bestDist = dist; bestX = x; bestGap = gap; }
      } else if (Number.isFinite(gap.left) && !Number.isFinite(gap.right)) {
        const x = gap.left + ROUTING_OBSTACLE_INFLATION;
        const dist = Math.abs(x - preferredX);
        if (dist < bestDist) { bestDist = dist; bestX = x; bestGap = gap; }
      }
      continue;
    }
    
    // Clamp preferredX into the gap
    const clampedX = Math.max(gap.left + MIN_GAP_PADDING, Math.min(preferredX, gap.right - MIN_GAP_PADDING));
    const dist = Math.abs(clampedX - preferredX);
    if (dist < bestDist) {
      bestDist = dist;
      bestX = clampedX;
      bestGap = gap;
    }
  }
  
  return { gap: bestGap, x: bestX };
}

/**
 * Find a clear Y position for horizontal segment.
 * Enhanced: finds actual gaps between nodes and routes through them.
 */
export function findClearY(preferredY, minY, maxY, minX, maxX, obstacles, edgeKey = null) {
  if (!pathBlockedByNodes(minX, preferredY, maxX, preferredY, obstacles)) {
    return preferredY;
  }
  
  // Find all horizontal gaps in the area
  const gaps = findHorizontalGaps(minX, maxX, obstacles);
  
  // Find the best gap that contains or is nearest to preferredY (within minY..maxY)
  let bestY = null;
  let bestDist = Infinity;
  
  for (const gap of gaps) {
    const gapHeight = gap.bottom - gap.top;
    
    if (!Number.isFinite(gap.top) && !Number.isFinite(gap.bottom)) continue;
    
    // For open-ended gaps, use the finite boundary
    let candidateY;
    if (!Number.isFinite(gap.top)) {
      candidateY = Math.min(preferredY, gap.bottom - MIN_GAP_PADDING);
    } else if (!Number.isFinite(gap.bottom)) {
      candidateY = Math.max(preferredY, gap.top + MIN_GAP_PADDING);
    } else if (gapHeight < MIN_GAP_PADDING * 2) {
      continue;
    } else {
      // Place in the center of the gap, biased toward preferredY
      candidateY = Math.max(gap.top + MIN_GAP_PADDING, Math.min(preferredY, gap.bottom - MIN_GAP_PADDING));
    }
    
    // Clamp to valid range
    candidateY = Math.max(minY, Math.min(candidateY, maxY));
    
    const dist = Math.abs(candidateY - preferredY);
    if (dist < bestDist && !pathBlockedByNodes(minX, candidateY, maxX, candidateY, obstacles)) {
      bestDist = dist;
      bestY = candidateY;
    }
  }
  
  if (bestY !== null) return bestY;
  
  // Fallback: original increment search
  for (let offset = 20; offset < 200; offset += 20) {
    const yUp = preferredY - offset;
    const yDown = preferredY + offset;
    
    if (yUp >= minY && !pathBlockedByNodes(minX, yUp, maxX, yUp, obstacles)) {
      return yUp;
    }
    if (yDown <= maxY && !pathBlockedByNodes(minX, yDown, maxX, yDown, obstacles)) {
      return yDown;
    }
  }
  
  return preferredY;
}

/**
 * Find clear X for vertical segment.
 * Enhanced: finds actual gaps between nodes and routes through them.
 */
export function findClearXForVertical(preferredX, y1, y2, obstacles, direction, edgeKey = null) {
  // Try the preferred position first
  if (!pathBlockedByNodes(preferredX, y1, preferredX, y2, obstacles)) {
    return preferredX;
  }
  
  // Find the best gap
  const { x: gapX } = findBestVerticalGap(preferredX, y1, y2, obstacles);
  if (gapX !== preferredX && !pathBlockedByNodes(gapX, y1, gapX, y2, obstacles)) {
    return gapX;
  }
  
  // Fallback: original increment search
  for (let offset = 0; offset < 300; offset += 30) {
    const testX = preferredX + (offset * direction);
    if (!pathBlockedByNodes(testX, y1, testX, y2, obstacles)) {
      return testX;
    }
    const testX2 = preferredX - (offset * direction);
    if (!pathBlockedByNodes(testX2, y1, testX2, y2, obstacles)) {
      return testX2;
    }
  }
  return preferredX;
}

/**
 * Find clear X for horizontal segment (used in horizontal routing)
 */
export function findClearXForHorizontal(preferredX, x1, x2, y, obstacles, direction = 1, edgeKey = null) {
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  
  if (!pathBlockedByNodes(minX, y, maxX, y, obstacles)) {
    return preferredX;
  }
  
  for (let offset = 30; offset < 300; offset += 30) {
    const testX = preferredX + (offset * direction);
    if (!pathBlockedByNodes(minX, y, testX, y, obstacles)) {
      return testX;
    }
    const testX2 = preferredX - (offset * direction);
    if (!pathBlockedByNodes(testX2, y, maxX, y, obstacles)) {
      return testX2;
    }
  }
  
  return preferredX;
}

/**
 * Find clear Y for horizontal segment in horizontal routing
 */
export function findClearYForHorizontal(preferredY, x, y1, y2, obstacles, direction = 1, edgeKey = null) {
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);
  
  if (!pathBlockedByNodes(x, minY, x, maxY, obstacles)) {
    return preferredY;
  }
  
  for (let offset = 30; offset < 300; offset += 30) {
    const testY = preferredY + (offset * direction);
    if (!pathBlockedByNodes(x, minY, x, testY, obstacles)) {
      return testY;
    }
    const testY2 = preferredY - (offset * direction);
    if (!pathBlockedByNodes(x, testY2, x, maxY, obstacles)) {
      return testY2;
    }
  }
  
  return preferredY;
}

/**
 * No longer using path cache
 */
export function clearPathCache(edgeKey) {
  // No-op
}

